import React, { useState } from 'react';
import { useAction, useQuery } from 'wasp/client/operations';
import { createLostFound, getBuySellFormMode, getSimplifiedFormEnabled, getOrganizationBySlug } from 'wasp/client/operations';
import { SimplifiedSubmitForm, SubmissionModeSwitch } from '../components/SimplifiedSubmit';
import { useAuth } from 'wasp/client/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { locationSectionPathFromLocation } from '../../client/lib/locationPaths';
import {
  ArrowLeft,
  Search,
  DollarSign,
  Tag,
  Phone,
  Save,
  AlertCircle,
  HelpCircle,
  Package,
  Star,
  MapPin,
  Calendar,
  Eye,
  EyeOff,
  FileText,
  User,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

interface LostFoundFormData {
  title: string;
  description: string;
  type: 'LOST_ITEM' | 'FOUND_ITEM';
  category: 'ELECTRONICS' | 'CLOTHING' | 'ACCESSORIES' | 'DOCUMENTS' | 'KEYS' | 'BOOKS' | 'SPORTS' | 'OTHER';
  locationFound: string;
  dateFound: string;
  contactInfo: string;
  reward: string;
  visibility: 'PUBLIC' | 'SUBSCRIBER' | 'VERIFIED';
}

const LOST_FOUND_TYPES = [
  { value: 'LOST_ITEM', label: 'Lost Item', icon: EyeOff, description: 'I lost something and need help finding it' },
  { value: 'FOUND_ITEM', label: 'Found Item', icon: Eye, description: 'I found something and want to return it' }
];

const CATEGORY_OPTIONS = [
  { value: 'ELECTRONICS', label: 'Electronics', description: 'Phones, laptops, tablets, etc.' },
  { value: 'CLOTHING', label: 'Clothing', description: 'Shirts, jackets, shoes, etc.' },
  { value: 'ACCESSORIES', label: 'Accessories', description: 'Jewelry, bags, watches, etc.' },
  { value: 'DOCUMENTS', label: 'Documents', description: 'IDs, passports, certificates, etc.' },
  { value: 'KEYS', label: 'Keys', description: 'House keys, car keys, etc.' },
  { value: 'BOOKS', label: 'Books', description: 'Textbooks, novels, notebooks, etc.' },
  { value: 'SPORTS', label: 'Sports', description: 'Equipment, gear, etc.' },
  { value: 'OTHER', label: 'Other', description: 'Anything else' }
];

const STEPS = [
  { id: 1, label: 'Report Type', icon: Search, description: 'Lost or found?' },
  { id: 2, label: 'Item Category', icon: Tag, description: 'Classify the item' },
  { id: 3, label: 'Item Details', icon: FileText, description: 'Describe the item' },
  { id: 4, label: 'Contact & Review', icon: User, description: 'Finalize and submit' }
];

const StepIndicator = ({ currentStep }: { currentStep: number }) => (
  <div className="w-full mb-8">
    <div className="relative flex items-center justify-between">
      <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200">
        <div className="h-full bg-blue-500 transition-all duration-500 ease-out" style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }} />
      </div>
      {STEPS.map((step) => {
        const Icon = step.icon;
        const isDone = currentStep > step.id;
        const isCurrent = currentStep === step.id;
        return (
          <div key={step.id} className="relative flex flex-col items-center z-10">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${isDone ? 'bg-blue-500 border-blue-500 text-white' : isCurrent ? 'bg-white border-blue-500 text-blue-600 shadow-md shadow-blue-100' : 'bg-white border-gray-200 text-gray-400'}`}>
              {isDone ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
            </div>
            <span className={`mt-2 text-xs font-medium hidden sm:block ${isCurrent ? 'text-blue-600' : isDone ? 'text-blue-400' : 'text-gray-400'}`}>{step.label}</span>
          </div>
        );
      })}
    </div>
    <div className="mt-4 text-center">
      <p className="text-sm text-gray-500">Step {currentStep} of {STEPS.length} — <span className="font-medium text-gray-700">{STEPS[currentStep - 1].description}</span></p>
    </div>
  </div>
);

// Tooltip component
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function AddLostFoundPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const { data: org } = useQuery(getOrganizationBySlug, { slug: slug! }, { enabled: !!slug }) as { data: any };

  const createLostFoundFn = useAction(createLostFound);
  const { data: formMode, isLoading: formModeLoading } = useQuery(getBuySellFormMode);
  const { data: simplifiedEnabled, isLoading: simplifiedEnabledLoading } = useQuery(getSimplifiedFormEnabled);

  const [submissionMode, setSubmissionMode] = useState<'full' | 'simplified'>('simplified');

  const defaultContact = [(user as any)?.phoneNumber, user?.email].filter(Boolean).join(' · ');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<LostFoundFormData>({
    title: '',
    description: '',
    type: 'LOST_ITEM',
    category: 'OTHER',
    locationFound: '',
    dateFound: '',
    contactInfo: defaultContact,
    reward: '',
    visibility: 'VERIFIED'
  });

  // Wizard step state
  const [currentStep, setCurrentStep] = useState(1);

  const handleChange = (field: keyof LostFoundFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!selectedLocation?.id) {
      newErrors.location = 'No location selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await createLostFoundFn({
        title: formData.title.trim(),
        description: formData.description.trim(),
        locationId: selectedLocation!.id,
        organizationId: org?.id,
        type: formData.type,
        category: formData.category,
        locationFound: formData.locationFound.trim(),
        dateFound: formData.dateFound ? new Date(formData.dateFound).toISOString() : undefined,
        contactInfo: formData.contactInfo.trim(),
        reward: formData.reward ? parseInt(formData.reward, 10) : undefined,
        visibility: formData.visibility,
        submissionType: 'STANDARD'
      });

      // Navigate back to the lost and found page
      handleGoBack();
    } catch (error) {
      console.error('Error creating lost/found item:', error);
      setErrors({ submit: 'Failed to create item. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(locationSectionPathFromLocation(selectedLocation, 'lost-found'));
  };

  const locationName = selectedLocation?.name || 'No location selected';
  const showModeSwitch = !!simplifiedEnabled;
  const effectiveMode: 'full' | 'simplified' = showModeSwitch ? submissionMode : 'full';

  const handleSimplifiedSubmit = async (description: string) => {
    if (!selectedLocation?.id) return;
    setIsSubmitting(true);
    try {
      const shortTitle = description.length > 60 ? description.slice(0, 57) + '…' : description;
      await createLostFoundFn({
        title: shortTitle, description, locationId: selectedLocation.id,
        type: 'LOST_ITEM', category: 'OTHER', locationFound: '',
        dateFound: undefined, contactInfo: defaultContact,
        reward: undefined, visibility: 'VERIFIED',
        submissionType: 'QUICK'
      });
      handleGoBack();
    } catch (error) {
      setErrors({ submit: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">You must be logged in to report lost/found items.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (formModeLoading || simplifiedEnabledLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (effectiveMode === 'simplified') {
    return (
      <DashboardLayout>
        <div className="overflow-y-auto h-full bg-gray-50">
          {showModeSwitch && <SubmissionModeSwitch activeMode={submissionMode} onSelectMode={setSubmissionMode} />}
          <SimplifiedSubmitForm isSubmitting={isSubmitting} onSubmit={handleSimplifiedSubmit} selectedLocationName={locationName} />
        </div>
      </DashboardLayout>
    );
  }

  const isWizard = formMode === 'wizard';

  if (isWizard) {
    const canAdvanceFrom1 = formData.title.trim().length > 0;
    const canAdvanceFrom2 = !!formData.category; // always true since it has a default
    const canAdvanceFrom3 = formData.description.trim().length > 0;

    const canAdvance =
      currentStep === 1 ? canAdvanceFrom1 :
      currentStep === 2 ? canAdvanceFrom2 :
      currentStep === 3 ? canAdvanceFrom3 :
      true;

    const handleNext = () => {
      if (currentStep < STEPS.length && canAdvance) {
        setCurrentStep(prev => prev + 1);
      }
    };

    const handleBack = () => {
      if (currentStep > 1) {
        setCurrentStep(prev => prev - 1);
      } else {
        handleGoBack();
      }
    };

    const handleWizardSubmit = () => {
      handleSubmit();
    };

    const selectedCategoryOption = CATEGORY_OPTIONS.find(c => c.value === formData.category);

    return (
      <DashboardLayout>
        {showModeSwitch && <SubmissionModeSwitch activeMode={submissionMode} onSelectMode={setSubmissionMode} />}
        <div className="min-h-screen bg-background">
          <div className="w-full max-w-2xl mx-auto px-4 py-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <Search className="w-7 h-7 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Post Lost/Found Item</h1>
                <p className="text-sm text-gray-500">{selectedLocation?.name}</p>
              </div>
            </div>

            <StepIndicator currentStep={currentStep} />

            {/* Step content */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 min-h-[360px]">
              {/* Step 1: Report Type + Title */}
              {currentStep === 1 && (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">What are you reporting?</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {LOST_FOUND_TYPES.map((type) => {
                        const isSelected = formData.type === type.value;
                        const isLost = type.value === 'LOST_ITEM';
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => handleChange('type', type.value as 'LOST_ITEM' | 'FOUND_ITEM')}
                            className={`relative p-3 sm:p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                              isSelected
                                ? isLost
                                  ? 'border-red-500 bg-gradient-to-br from-red-50 to-rose-50'
                                  : 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            {isSelected && (
                              <CheckCircle2 className={`absolute top-3 right-3 w-4 h-4 ${isLost ? 'text-red-500' : 'text-green-500'}`} />
                            )}
                            <div className="font-semibold text-gray-900 text-sm mb-1">{type.label}</div>
                            <div className="text-xs text-gray-500">{type.description}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Item Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="e.g., iPhone 13 Pro, Black Jacket, Car Keys"
                    />
                    {errors.title && (
                      <p className="text-red-600 text-sm mt-1">{errors.title}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Category + Location + Date */}
              {currentStep === 2 && (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">Select a category</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {CATEGORY_OPTIONS.map((cat) => {
                        const isSelected = formData.category === cat.value;
                        return (
                          <button
                            key={cat.value}
                            type="button"
                            onClick={() => handleChange('category', cat.value as LostFoundFormData['category'])}
                            className={`relative p-3 sm:p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                              isSelected
                                ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            {isSelected && (
                              <CheckCircle2 className="absolute top-3 right-3 w-4 h-4 text-blue-500" />
                            )}
                            <div className="font-medium text-gray-900 text-xs sm:text-sm">{cat.label}</div>
                            <div className="text-xs text-gray-400 mt-0.5 hidden sm:block">{cat.description}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location {formData.type === 'LOST_ITEM' ? 'Lost' : 'Found'}
                    </label>
                    <input
                      type="text"
                      value={formData.locationFound}
                      onChange={(e) => handleChange('locationFound', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Where was it found/lost?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date {formData.type === 'LOST_ITEM' ? 'Lost' : 'Found'}
                    </label>
                    <input
                      type="date"
                      value={formData.dateFound}
                      onChange={(e) => handleChange('dateFound', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Description + Reward */}
              {currentStep === 3 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      rows={4}
                      maxLength={255}
                      onChange={(e) => {
                        handleChange('description', e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none overflow-hidden ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Provide detailed information about the item, including any distinguishing features, condition, etc..."
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">{formData.description.length}/255 characters</p>
                    {errors.description && (
                      <p className="text-red-600 text-sm mt-1">{errors.description}</p>
                    )}
                  </div>

                  {formData.type === 'LOST_ITEM' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reward (Optional)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-sm">$</span>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={formData.reward}
                          onChange={(e) => handleChange('reward', e.target.value)}
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Contact + Review */}
              {currentStep === 4 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Information</label>
                    <input
                      type="text"
                      value={formData.contactInfo}
                      onChange={(e) => handleChange('contactInfo', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Phone number or email"
                    />
                    <p className="text-xs text-gray-400 mt-1">Pre-filled from your account — edit if needed.</p>
                  </div>

                  {/* Summary card */}
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-2">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Summary</h3>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div>
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Type</span>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">
                          {formData.type === 'LOST_ITEM' ? 'Lost Item' : 'Found Item'}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Category</span>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{selectedCategoryOption?.label || '—'}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Title</span>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{formData.title || '—'}</p>
                      </div>
                      {formData.locationFound && (
                        <div>
                          <span className="text-xs text-gray-500 uppercase tracking-wide">Location</span>
                          <p className="text-sm text-gray-700 mt-0.5">{formData.locationFound}</p>
                        </div>
                      )}
                      {formData.dateFound && (
                        <div>
                          <span className="text-xs text-gray-500 uppercase tracking-wide">Date</span>
                          <p className="text-sm text-gray-700 mt-0.5">{formData.dateFound}</p>
                        </div>
                      )}
                      {formData.type === 'LOST_ITEM' && formData.reward && (
                        <div>
                          <span className="text-xs text-gray-500 uppercase tracking-wide">Reward</span>
                          <p className="text-sm text-gray-700 mt-0.5">${formData.reward}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Approval notice */}
                  <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>All postings go through an approval process before being published.</span>
                  </div>

                  {errors.submit && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                      <p className="text-red-800 text-sm">{errors.submit}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Navigation bar */}
            <div className="flex items-center justify-between mt-5">
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-2 px-4 py-2.5 text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                {currentStep === 1 ? 'Cancel' : 'Back'}
              </button>

              {currentStep < STEPS.length ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!canAdvance}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleWizardSubmit}
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Submit Report
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Mini dot indicators */}
            <div className="flex justify-center gap-1.5 mt-4">
              {STEPS.map((step) => (
                <div
                  key={step.id}
                  className={`h-1.5 rounded-full transition-all duration-300 ${currentStep === step.id ? 'w-5 bg-blue-500' : currentStep > step.id ? 'w-1.5 bg-blue-300' : 'w-1.5 bg-gray-300'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Default single-page form (unchanged)
  return (
    <DashboardLayout>
      {showModeSwitch && <SubmissionModeSwitch activeMode={submissionMode} onSelectMode={setSubmissionMode} />}
      <div className="flex h-full">
        {/* Main Content Area - Takes full width excluding left sidebar */}
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className="flex-1 p-6 pb-32 lg:pb-6">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Search className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Report Lost/Found Item</h1>
                    <p className="text-gray-600">Location: {selectedLocation?.name || 'No location selected'}</p>
                  </div>
                </div>
              </div>

              <form id="lost-found-form" onSubmit={handleSubmit}>
                {/* Full-width sections */}
                <div className="space-y-6 mb-6">
                  {/* Item Type Selection */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">What are you reporting?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {LOST_FOUND_TYPES.map((type) => {
                        const IconComponent = type.icon;
                        return (
                          <label
                            key={type.value}
                            className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                              formData.type === type.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="type"
                              value={type.value}
                              checked={formData.type === type.value}
                              onChange={(e) => handleChange('type', e.target.value as 'LOST_ITEM' | 'FOUND_ITEM')}
                              className="sr-only"
                            />
                            <div className="flex items-center gap-3">
                              <IconComponent className={`w-6 h-6 ${formData.type === type.value ? 'text-blue-600' : 'text-gray-400'}`} />
                              <div>
                                <div className="font-medium text-gray-900">{type.label}</div>
                                <div className="text-sm text-gray-500">{type.description}</div>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Item Title */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Item Description</h3>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.title ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder="e.g., iPhone 13 Pro, Black Jacket, Car Keys"
                      required
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.title}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Detailed Description</h3>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.description ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder="Provide detailed information about the item, including any distinguishing features, condition, etc..."
                      required
                    />
                    {errors.description && (
                      <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.description}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">0/1000 characters</p>
                  </div>
                </div>

                {/* Two-column layout for form fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleChange('category', e.target.value as any)}
                        className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                      >
                        {CATEGORY_OPTIONS.map(category => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Location Found */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Location {formData.type === 'LOST_ITEM' ? 'Lost' : 'Found'}</span>
                          <Tooltip content={`Where the item was ${formData.type === 'LOST_ITEM' ? 'last seen' : 'found'}`}>
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={formData.locationFound}
                          onChange={(e) => handleChange('locationFound', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="e.g., Parking lot, Lobby, Room 205"
                        />
                      </div>
                    </div>

                    {/* Date Found */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Date {formData.type === 'LOST_ITEM' ? 'Lost' : 'Found'}</span>
                          <Tooltip content={`When the item was ${formData.type === 'LOST_ITEM' ? 'lost' : 'found'}`}>
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="date"
                          value={formData.dateFound}
                          onChange={(e) => handleChange('dateFound', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Contact Information */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Contact Information</span>
                          <Tooltip content="How people can reach you about this item">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={formData.contactInfo}
                          onChange={(e) => handleChange('contactInfo', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="Phone number or email"
                        />
                      </div>
                    </div>

                    {/* Reward */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Reward (Optional)</span>
                          <Tooltip content="Optional reward amount for returning the item">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.reward}
                          onChange={(e) => handleChange('reward', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error message */}
                {errors.submit && (
                  <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}
              </form>
            </div>

            {/* Right Sidebar - Anchored to the right */}
            <div className="hidden lg:flex w-80 border-l border-border flex-col">
              {/* Action Buttons */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="lost-found-form"
                    onClick={(e) => handleSubmit(e as any)}
                    disabled={isSubmitting || !formData.title.trim()}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Submit Report
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleGoBack}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Approval Notice */}
              <div className="p-6 border-b border-border">
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>All postings go through an approval process before being published.</span>
                </div>
              </div>

              {/* Tips for Better Results */}
              <div className="p-6 flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tips for Better Results</h3>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Be specific about the item's appearance and any unique features</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Include the exact location where the item was lost/found</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Provide accurate contact information for easy communication</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Consider offering a reward to increase the chances of recovery</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Be responsive to inquiries to help facilitate the return</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Action Bar */}
      <div className="fixed bottom-14 left-0 right-0 z-10 lg:hidden bg-card border-t border-border px-4 py-3 shadow-lg">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleGoBack}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="lost-found-form"
            onClick={(e) => handleSubmit(e as any)}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : 'Post'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}



